import { Location } from '@angular/common';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import * as moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { DynamicContentModalComponent } from '@Work/Accounts/proprietary/dynamic-content-outlet/dynamic-content-modal.component';
import { AppService } from '@Core/services/proprietary/app.service';
import { SessionStorage } from '@Core/services/proprietary/browser-storage/session-storage.service';
import { ConfirmationDialogService } from '@Core/services/proprietary/confirmation-dialog/confirmation-dialog.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { DataSourceStatus, Datasource } from '@Core/services/proprietary/datasource';
import { ObjectRuleService } from '@Core/services/proprietary/object-rule/object-rule.service';
import {
    CompiledObjectRules,
    EvaluatedStateObject,
    ObjectRuleSet,
    RuleNode
} from '@Core/services/proprietary/object-rule/object-rule.types';
import { ObjectService } from '@Core/services/proprietary/object.service';
import { PageParameterService } from '@Core/services/proprietary/page-parameter.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { TaskObjectState } from '../task/task-object.state';
import { LockEventType, LockObject, LockObjectEditStatusType, UserDefinedValidation } from './page-object.types';
import {
    AddDesignerElementRule,
    AddObjectRuleSets,
    AddPageViews,
    AddSections,
    AddToComponentSettingList,
    AddValidEvent,
    DeleteDesignerElementRule,
    DeleteSection,
    ExpandCollapseChildSections,
    HighlightErrors,
    ReplaceChildSections,
    ResetErrors,
    ResetLock,
    ResetObjectRules,
    ResetState,
    RestoreValidationRules,
    SaveValidationRules,
    SelectSection,
    SetAllSectionsReadOnly,
    SetAllSectionsVisible,
    SetAnticipatedValidationEvent,
    SetCompiledRules,
    SetCoreSections,
    SetCurrentDesignerComponent,
    SetCurrentDesignerComponentEditMode,
    SetCurrentDesignerElement,
    SetCurrentPageView,
    SetCurrentSection,
    SetCurrentSectionByTitle,
    SetDesignerMode,
    SetDirty,
    SetEvaluatedStateObjects,
    SetLock,
    SetMandatoryEvents,
    SetManualNavigationMode,
    SetMissingEvents,
    SetNavigatorStyle,
    SetObjectValidationEvents,
    SetPageViewSections,
    SetPendingDesignerRuleChange,
    SetPermittedEvents,
    SetPristine,
    SetProtectedMode,
    SetReadOnlyMode,
    SetSingleSectionMode,
    SetUserDefinedValidations,
    SetValidEvents,
    TrackUnsavedChanges,
    UpdateComponentSetting,
    UpdateDesignerElementRule,
    UpdatePageModel,
    UpdateSectionComponentSettingLink,
    UpdateSectionData,
    UpdateSectionProperties,
    UpdateSectionTitle,
    UpdateSectionUxConfig,
    UpdateSectionUxConfigComponentSettings
} from './page.actions';
import {
    ComponentSetting,
    ComponentSettingLink,
    DesignerComponent,
    DesignerElement,
    DesignerElementPropertyType,
    EventType,
    NavigatorStyleType,
    PageSection,
    PageState,
    PageView
} from './page.state';
import { DatePipe } from '@Shared/Pipes/proprietary/date.pipe';
import { AlertDialogService } from '@Core/services/proprietary/alert-dialog/alert-dialog.service';

@Injectable()
export class PageObjectService implements OnDestroy {
    constructor(
        private store: Store,
        private objectRuleService: ObjectRuleService,
        private os: ObjectService,
        private pageParameterService: PageParameterService,
        private ds: DataService,
        private app: AppService,
        private modalService: NgbModal,
        private injector: Injector,
        private helper: StateHelperService,
        private alertDialog: AlertDialogService,
        private confirmationDialog: ConfirmationDialogService,
        private sessionStorage: SessionStorage,
        private location: Location,
        private router: Router,
        private epDate: DatePipe
    ) {
        this.dsLock = this.ds.addNewDatasource('Lock', 'NO-CACHE');
        this.dsLock.showErrors = false; //Don't display if LockProcess fails.
        this.dsProductLineArticle = this.ds.addNewDatasource('DBQuery', 'NO-CACHE');
        window.addEventListener('storage', this.proceedWithExistingLockChanged.bind(this), true);
    }

    private pageID: string = null;
    private dsLock: Datasource;
    private dsProductLineArticle: Datasource;
    private dsProductLineArticleSub: Subscription;
    private dsLockSub: Subscription;
    private dsPageLayoutSub: Subscription;
    private dsPageLayoutCollectionSub: Subscription;
    private dsPageLayoutProcessSub: Subscription;
    private dsComponentSettingCollectionSub: Subscription;
    private dsComponentSettingGetSub: Subscription;
    private pageViewList: any[] = [];
    private objectCode: string = null;
    private baseObjectCode: string = null;
    private _systemPageView: PageView = null;
    manualEditTracker = new Subject();
    modalRef: NgbModalRef;
    private callbackFunction: any;
    private reloadLayoutObjectCode: string = null;

    get currentRoute() {
        let urlData = this.router.url.split(';');
        let currentURL = urlData[0];
        return currentURL;
    }

    get systemPageView(): PageView {
        return this._systemPageView;
    }

    ngOnDestroy() {
        window.removeEventListener('storage', this.proceedWithExistingLockChanged.bind(this), true);
    }

    addSections(sections: PageSection[], overwrite: boolean = true, insertAfterSectionID: number = null) {
        this.store.dispatch(new AddSections(sections, overwrite, insertAfterSectionID));
    }

    deleteSection(sectionID: number) {
        this.store.dispatch(new DeleteSection(sectionID));
    }

    replaceChildSections(ParentSectionID: number, sections: PageSection[]) {
        this.store.dispatch(new ReplaceChildSections(ParentSectionID, sections));
    }

    expandCollapseChildSections(sectionID: number) {
        this.store.dispatch(new ExpandCollapseChildSections(sectionID));
    }

    addPageViews(pageViews: PageView[], autoAddSections: boolean = true) {
        this.store.dispatch(new AddPageViews(pageViews, autoAddSections, this.reloadLayoutObjectCode));
        // Clear out after dispatching AddPageViews. Otherwise this never get cleared and forces the use as the last edit layout when going to a page.
        this.reloadLayoutObjectCode = null;
    }

    getUXDesignerBaseObject(): string {
        return this.baseObjectCode;
    }

    getProductLineArticleConfig(): any[] {
        return this.dsProductLineArticle.objectNodes || [];
    }

    async loadPageViews(
        objectCode: string,
        systemPageView: PageView,
        additionalSections: PageSection[] = null,
        additionalRules: ObjectRuleSet[] = null,
        autoAddSections: boolean = true
    ) {
        //load system page view and user defined pageviews from database (based on base objectcode)
        //User defined page views will inherit the rules specified on the system page view plus add thier own rules

        //additionalSections is used to add sections local to the page (e.g. contract procedure sections). 
        // For now, we are not allowing editing of those sections.

        let parentObjectCode = objectCode;
        switch (objectCode.toLowerCase()) {
            case 'applicatn':
            case 'market':
            case 'quote':
            case 'bind':
            case 'issue':
            case 'endorse':
            case 'cancsched':
            case 'cancel':
            case 'reinstate':
            case 'selfaudit':
            case 'audit':
            case 'extendterm':
            case 'renew':
            case 'reissue':
            case 'rewrite':
            case 'disablecon':
            case 'consumchg':
            case 'nonrenew':
            case 'rate':
                parentObjectCode = 'Contract';
                break;
            default:
            //currently, all other objects are the parent object.
        }
        this.baseObjectCode = parentObjectCode;
        this.objectCode = objectCode;

        this._systemPageView = cloneDeep(systemPageView);
        if (!this._systemPageView.DynamicSections) {
            this._systemPageView.DynamicSections = []; // initialize to empty array if null
        }

        if (additionalSections) {
            //prepend the additional sections
            this._systemPageView.Sections = [...additionalSections, ...this._systemPageView.Sections];
        }

        if (additionalRules) {
            //add additional rules
            this._systemPageView.RuleSets = [...additionalRules, ...this._systemPageView.RuleSets];
        }

        this.setCoreSections([...this._systemPageView.Sections, ...this._systemPageView.DynamicSections]);

        if (this.app.DesignerMode && this.baseObjectCode == 'Contract') {
            await this.retrieveProductLineArticle();
            this.filterLineSpecificArticleSections();
        }

        let tempPageViews = [];

        await this.getPageLayoutCollection();

        let includeFullSystemView: boolean = false;

        // loop through list of page views and retrieve views  (SHOULD WE RETRIEVE ALL NOW OR WHEN THE USER NAVIGATES TO THE VIEW?)
        for (let index = 0; index < this.pageViewList.length; index++) {
            const layoutObjectCode = this.pageViewList[index].Layout_Object_Code;

            if (layoutObjectCode.startsWith('PLFS')) {
                //Full System Objects always start with PLFS.
                includeFullSystemView = true;

                if (this.app.DesignerMode) {
                    this._systemPageView.View_ID = 9999; // force system view to the end of the list in UX designer
                } else {
                    this._systemPageView.Layout_Object_Code = layoutObjectCode;
                    this._systemPageView.View_ID = index + 1;
                }
            } else {
                if (await this.getPageLayout(layoutObjectCode)) {
                    if (this.app.dsPageLayoutGet.objectNode) {
                        let tempPageView = cloneDeep(this.app.dsPageLayoutGet.objectNode);

                        // state pageview does not have Sections/Section structure.  Just has Section array
                        if (tempPageView.Sections?.Section) {
                            tempPageView.Sections = tempPageView.Sections.Section;
                        }
                        if (tempPageView.Sections == '') {
                            tempPageView.Sections = [];
                        }
                        if (tempPageView.Component_Settings == '') {
                            tempPageView.Component_Settings = [];
                        }
                        tempPageView.Title = tempPageView.Layout_Name;
                        delete tempPageView.Layout_Name;

                        // remove the superfulous ComponentSetting node
                        tempPageView.ComponentSettings = tempPageView.ComponentSettings?.ComponentSetting || [];

                        // base64 decode setting_syntax on component settings
                        tempPageView.ComponentSettings.forEach((element) => {
                            try {
                                element.Setting_Syntax = JSON.parse(atob(element.Setting_Syntax));
                            } catch {
                                element.Setting_Syntax = {};
                            }
                        });

                        //rename Section_Order to Display_Order
                        //rename Section_Name to Title
                        //rename Component_Name to Component_Type
                        //rename Component_Settings to Ux_Config (and base64 decode)
                        //Section ID in state is not the same as the Section ID in the database/sdk - delete it
                        //save of Section ID from SDK/Database for update
                        //DELETE OF SETTING_SYNTAX - STATE STILL USING UX_CONFIG
                        tempPageView.Sections.forEach((section) => {
                            section.DB_Section_ID = section.Section_ID;
                            section.Title = section.Section_Name;
                            section.Component_Type = section.Component_Name;
                            section.Display_Order = section.Section_Order;
                            try {
                                section.Ux_Config = JSON.parse(atob(section.Setting_Syntax));
                            } catch {
                                section.Ux_Config = {};
                            }
                            delete section.Section_ID;
                            delete section.Section_Name;
                            delete section.Component_Name;

                            delete section.Section_Order;
                            delete section.Setting_Syntax;

                            const coreSection = this.store.selectSnapshot(PageState.coreSectionByGUID)(section.Core_Section_GUID);
                            if (coreSection) {
                                if (this.baseObjectCode == 'Contract') {
                                    if (coreSection.Article_Type) {
                                        section.Article_Type = coreSection.Article_Type;
                                    }
                                }
                            }

                            //LOAD COMPONENT SETTINGS INTO THE SECTION UX_CONFIG 
                            // -- May be handled differently when we support sharing component settings  $$$$$

                            // remove the superfulous ComponentSetting node
                            section.ComponentSettings = section.ComponentSettings?.ComponentSetting || [];

                            if (section.ComponentSettings) {
                                section.ComponentSettings.forEach((sectionComponent) => {
                                    let settingID = sectionComponent.Setting_ID;
                                    let componentName = sectionComponent.Component_Name;

                                    // Find the component setting
                                    let setting = null;

                                    setting = tempPageView.ComponentSettings.find((element) => element.Setting_ID == settingID);

                                    if (setting) {
                                        if (!section.Ux_Config) {
                                            section.Ux_Config = {};
                                        }
                                        section.Ux_Config[componentName] = setting.Setting_Syntax;
                                        //ADD Setting_ID to UX_Config Components to support update of components in designer
                                        section.Ux_Config[componentName].Setting_ID = settingID;
                                    } else {
                                        console.error(`Component_Setting not found [Setting_ID=${settingID}]`);
                                    }
                                });
                            }
                        });

                        let pageView: PageView = tempPageView;

                        // add rulesets from the systemPageView to each user pageview
                        if (this._systemPageView.RuleSets) {
                            if (!pageView.RuleSets) {
                                pageView.RuleSets = [];
                            }
                            pageView.RuleSets = [...this._systemPageView.RuleSets, ...pageView.RuleSets];
                        }
                        // set the view ID to the order of the user views
                        pageView.View_ID = index + 1;
                        if (additionalSections) {
                            //prepend the additional sections
                            pageView.Sections = [...additionalSections, ...pageView.Sections];
                        }
                        tempPageViews.push(pageView);
                    } else {
                        console.error(`Page Layout Not Retrieved.  LayoutObjectCode=${layoutObjectCode}`);
                    }
                }
            }
        }

        if (!includeFullSystemView && this.app.DesignerMode) {
            includeFullSystemView = true;
        }

        //Dynamically include system page view
        if (includeFullSystemView) {
            tempPageViews = [...tempPageViews, this._systemPageView];
        }

        if (tempPageViews.length > 0) {
            tempPageViews.sort((a, b) => a.View_ID - b.View_ID);
            this.addPageViews(tempPageViews, autoAddSections);
        } else {
            //   if (!this.app.DesignerMode) {
            //     this.app.showAlert({
            //       title: 'No Page Views',
            //       message: 'You do not have rights to any views for this page.'
            //     });
            //     this.location.back();
            //   }
        }
    }

    private filterLineSpecificArticleSections() {
        if (this.app.DesignerMode) {
            // UX Designer: Remove all Line specific articles. Dynamically added back based on product line article config.
            let originalSections = cloneDeep(this._systemPageView.Sections);
            let findIdx = -1;
            for (let sectionIdx = this._systemPageView.Sections.length - 1; sectionIdx >= 0; sectionIdx--) {
                const currentSection = this._systemPageView.Sections[sectionIdx];
                if (
                    currentSection.Article_Type &&
                    currentSection.Section_Group == 'LINE' &&
                    currentSection.Section_Key != '<ALL>'
                ) {
                    this._systemPageView.Sections.splice(sectionIdx, 1);
                }
            }

            for (var prodLineArtIdx in this.dsProductLineArticle.objectNodes) {
                const currentConfig = this.dsProductLineArticle.objectNodes[prodLineArtIdx];

                //First look for an exact match section.
                findIdx = originalSections.findIndex(
                    (findSection) =>
                        findSection.Section_Group == 'LINE' &&
                        findSection.Article_Type == currentConfig.Article_Type &&
                        findSection.Section_Key.toUpperCase() == currentConfig.Line_Code.toUpperCase()
                );

                if (findIdx > -1) {
                    // Exact match found - Push the original section as is.
                    this._systemPageView.Sections.push(originalSections[findIdx]);
                } else {
                    //If exact match isn't found, find the first section for the article type
                    findIdx = originalSections.findIndex(
                        (findSection) =>
                            findSection.Section_Group == 'LINE' && findSection.Article_Type == currentConfig.Article_Type
                    );
                    if (findIdx > -1) {
                        // Article Section Found - Push the original section, but merged with the currentConfig's Line_Code as the Section_Key.
                        this._systemPageView.Sections.push({
                            ...originalSections[findIdx],
                            ...{ Section_Key: currentConfig.Line_Code }
                        });
                    }
                }
            }
        }
    }

    async loadComponentSettingList(componentName: string) {
        return new Promise(async (resolve) => {
            //this.componentSettingList = [];
            if (this.dsComponentSettingCollectionSub) {
                this.dsComponentSettingCollectionSub.unsubscribe();
            }

            //TODO: Move this datasource inside of this service rather than in App service
            this.app.dsComponentSettingCollection.getCollection({
                json: 'CT2',
                View: 'Full',
                Parent_Object_Code: this.baseObjectCode,
                Component_Name: componentName
            });

            this.dsComponentSettingCollectionSub = this.app.dsComponentSettingCollection.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        if (this.dsComponentSettingCollectionSub) {
                            this.dsComponentSettingCollectionSub.unsubscribe();
                        }
                        this.store.dispatch(new AddToComponentSettingList(this.app.dsComponentSettingCollection.objectNodes, true));
                        resolve(true);
                        break;

                    case DataSourceStatus.Error:
                        if (this.dsComponentSettingCollectionSub) {
                            this.dsComponentSettingCollectionSub.unsubscribe();
                        }
                        resolve(false);
                        break;
                }
            });
        });
    }

    async loadComponentSetting(settingID: string) {
        return new Promise(async (resolve) => {
            if (this.dsComponentSettingGetSub) {
                this.dsComponentSettingGetSub.unsubscribe();
            }

            //TODO: Move this datasource inside of this service rather than in App service
            this.app.dsComponentSettingGet.get({
                json: 'CT2',
                View: 'Full',
                Setting_ID: settingID
            });

            this.dsComponentSettingGetSub = this.app.dsComponentSettingGet.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        if (this.dsComponentSettingGetSub) {
                            this.dsComponentSettingGetSub.unsubscribe();
                        }
                        resolve(true);
                        break;

                    case DataSourceStatus.Error:
                        if (this.dsComponentSettingGetSub) {
                            this.dsComponentSettingGetSub.unsubscribe();
                        }
                        resolve(false);
                        break;
                }
            });
        });
    }

    highlightErrors(value: boolean = true) {
        // TODO:  move into a PageObjectService
        value ? this.store.dispatch(new HighlightErrors()) : this.store.dispatch(new ResetErrors());
    }

    setReadOnlyMode(value: boolean = true) {
        this.store.dispatch(new SetReadOnlyMode(value));
    }

    newObject() {
        this.store.dispatch(new SetReadOnlyMode(false));
    }

    setProtectedMode(value: boolean = true, ignoreTaskState: boolean = false) {
        //Indicates that the object "cannot" be edited.   ReadOnly indicates whether in edit mode or not.   Can't turn off readonly if protected
        let protectedMode = value;
        if (!ignoreTaskState && !protectedMode) {
            // if the current task has been resolved (done or skipped) then force protected
            let current = this.store.selectSnapshot(TaskObjectState.currentInfo);
            if (current.Task_Resolved) {
                protectedMode = true;
            }
        }
        this.store.dispatch(new SetProtectedMode(protectedMode));
    }

    setEffectiveRights(
        taskID: string,
        objectCode: string,
        wfStep: any = null,
        taskResolved: boolean = false,
        myTask: boolean = true
    ) {
        let isProtected: boolean = false;
        let isReadOnly: boolean = false;
        let isWorkflowStep: boolean = true;
        let isNewTask: boolean = taskID == '-1';
        let mandatoryEvents: EventType[] = [];
        let permittedEvents: EventType[] = [];
        let userRights: any = {};
        let isWFStartObject: boolean = false;

        let objectConfig = this.os.getObjectConfig(objectCode);
        if (objectConfig && objectConfig.User_Rights) {
            userRights = objectConfig.User_Rights;
            if (objectConfig.Object_Class == 'W') {
                isWFStartObject = true;
            }
        }

        if (wfStep == null) {
            isWorkflowStep = false;
            wfStep = {};
        }

        if (taskResolved) {
            isProtected = true;
        }

        if (isWFStartObject) {
            if (userRights.Submit_Ind != 'Y') {
                //must have submit right to start a workflow
                isProtected = true;
            }
        } else {
            if (userRights.View_Object_Ind != 'Y') {
                // Object should have never been able to be opened.   This is an secondary safeguard to make sure its not editable
                isProtected = true;
            }

            if (isNewTask && (userRights.Create_Ind == 'N' || (!isWorkflowStep && userRights.Adhoc_Use_Ind == 'N'))) {
                // New Task, but no creation rights OR not part of a workflow step and no adhoc use right
                // Note: Object should have never been able to be opened.   This is a secondary safeguard.
                isProtected = true;
            }
        }

        if (userRights.Modify_Ind == 'N' || wfStep.Modify_Use == 'N') {
            isProtected = true;
        }

        if (wfStep.Object_Open_Mode == 'R') {
            isReadOnly = true;
        }

        if (wfStep.Object_Open_Mode == 'P') {
            isProtected = true;
        }

        if (!myTask) {
            isReadOnly = true;
        }

        //TODO: Add processing for checking if the object is locked.
        //      Consider adding the ability to return the lock status of an object (lock user id/name)
        //      related to a task in the TaskGet transaction.   We could then
        //      pass the lock status into this function and set ReadOnly if the item
        //      is locked by another user.    If they click "Edit" we would re-retrieve
        //      and try to get the lock.    We may want the first re-retrieve the task
        //      to see if still locked by the other user.

        if (isNewTask && !isWorkflowStep && !isWFStartObject) {
            // If this is an adhoc object open then open the object in readonly mode
            // --- NOTE: INDIVIDUAL OBJECT PAGES/SERVICES MAY SET THIS READONLY OFF FOR "NEW" OBJECTS ---
            isReadOnly = true;
        }

        if (isReadOnly) {
            this.setReadOnlyMode(true);
        }
        if (isProtected) {
            this.setProtectedMode(true);
        }

        this.addEvent(EventType.Create, userRights.Create_Ind, wfStep.Create_Use, permittedEvents, mandatoryEvents);
        //In Sure MGA, MODIFY is referred to as UPDATE
        this.addEvent(EventType.Update, userRights.Modify_Ind, wfStep.Modify_Use, permittedEvents, mandatoryEvents);
        if (
            [
                'contract',
                'applicatn',
                'market',
                'quote',
                'bind',
                'issue',
                'endorse',
                'audit',
                'selfaudit',
                'cancel',
                'reinstate',
                'extendterm',
                'renew',
                'reissue',
                'rewrite',
                'rate'
            ].includes(objectCode.toLowerCase())
        ) {
            //There is no specific "RATE" permission - if the user has update rights and 
            // the object is a contract procedure then add the RATE Event permission
            this.addEvent(EventType.Rate, userRights.Modify_Ind, wfStep.Modify_Use, permittedEvents, mandatoryEvents);
        }

        if (
            [
                'receipt',
                'reconcile',
                'deposit',
                'disburse',
                'jrnlentry',
                'periodend',
                'bill',
                '13thperiod',
                'balforward'
            ].includes(objectCode.toLowerCase())
        ) {
            //Accounting procedure objects not setup w/ Method_Send_Ind, use Post right for determining if Distributing is allowed.
            this.addEvent(EventType.Send, userRights.Post_Ind, wfStep.Post_Use, permittedEvents, mandatoryEvents);
        }

        if (objectCode.toLowerCase() == 'offsetdef') {
            if (userRights.Full_Access_Ind == 'Y' || userRights.Adhoc_Use_Ind == 'Y' || isWorkflowStep) {
                this.addEvent(EventType.Post, 'Y', isWorkflowStep ? 'O' : null, permittedEvents, mandatoryEvents);
            }
        }

        if (objectCode.toLowerCase() == 'lockbox') {
            if (userRights.Full_Access_Ind == 'Y' || userRights.Adhoc_Use_Ind == 'Y' || isWorkflowStep) {
                this.addEvent(EventType.Post, 'Y', isWorkflowStep ? 'O' : null, permittedEvents, mandatoryEvents);
                this.addEvent(EventType.Delete, 'Y', isWorkflowStep ? 'O' : null, permittedEvents, mandatoryEvents);
            }
        }

        if (['certprop', 'cert'].includes(objectCode.toLowerCase())) {
            this.addEvent(EventType.Send, userRights.Modify_Ind, wfStep.Send_Use, permittedEvents, mandatoryEvents);
        }

        if (objectCode.toLowerCase() == 'qecontract') {
            permittedEvents.push(EventType.Update);
        }

        this.addEvent(EventType.Delete, userRights.Delete_Ind, wfStep.Delete_Use, permittedEvents, mandatoryEvents);
        if (isWFStartObject) {
            // 'Start' event is used for WF Start Object, but rights are defined using 'Submit'
            this.addEvent(EventType.Start, userRights.Submit_Ind, wfStep.Submit_Use, permittedEvents, mandatoryEvents);
        } else {
            this.addEvent(EventType.Submit, userRights.Submit_Ind, wfStep.Submit_Use, permittedEvents, mandatoryEvents);
        }
        this.addEvent(EventType.Accept, userRights.Accept_Ind, wfStep.Accept_Use, permittedEvents, mandatoryEvents);
        this.addEvent(EventType.Reject, userRights.Reject_Ind, wfStep.Reject_Use, permittedEvents, mandatoryEvents);
        this.addEvent(EventType.Post, userRights.Post_Ind, wfStep.Post_Use, permittedEvents, mandatoryEvents);
        this.addEvent(EventType.Void, userRights.Void_Ind, wfStep.Void_Use, permittedEvents, mandatoryEvents);
        this.addEvent(EventType.Approve, userRights.Approve_Ind, wfStep.Approve_Use, permittedEvents, mandatoryEvents);
        this.addEvent(EventType.Search, userRights.Search_Ind, wfStep.Search_Use, permittedEvents, mandatoryEvents);
        //****************************************************************************************************************
        //TODO: The userRights.Print_Ind property in the line below needs to be changed to Send_Ind once the TaskGet transaction
        //is updated to return the Send_Ind property under User_Rights.   It seems to have been omitted.
        //Distribute is managed through send right
        this.addEvent(EventType.Send, userRights.Print_Ind, wfStep.Send_Use, permittedEvents, mandatoryEvents);
        //****************************************************************************************************************
        this.addEvent(EventType.File, userRights.File_Ind, wfStep.File_Use, permittedEvents, mandatoryEvents);
        this.addEvent(EventType.Print, userRights.Print_Ind, wfStep.Print_Use, permittedEvents, mandatoryEvents);

        if (isWorkflowStep) {
            this.wfEventCleanUp(objectCode, wfStep, permittedEvents, mandatoryEvents);
        }

        this.setPermittedEvents(permittedEvents);
        this.setMandatoryEvents(mandatoryEvents);

        //Other Rights in Object Rights that are not tied to a specific event
        //===================================================================
        //Full_Access_Ind  -- NO NEED TO IMPLEMENT SINCE INDIVIDUAL RIGHTS WILL BE TRUE IF FULL_ACCESS_IND = "Y"

        //Other Event Types in Sure MGA UI (Without WF Step or Object Rights)
        //=============================================================
        //Complete -> only used for project complete.  probably doesn't need any handling here
        //Skip -> only used by task skip.  probably doesn't need to any handling here
        //Rate -> rate event does not have rights settings in security -- If they can save, they can rate.
    }

    private addEvent(
        event: EventType,
        userRightInd: string,
        wfUse: string,
        permittedEventList: EventType[],
        mandatoryEventList: EventType[]
    ) {
        if (wfUse == 'M') {
            //add to mandatory event list
            mandatoryEventList.push(event);
        }

        if (userRightInd && userRightInd == 'Y') {
            if (!wfUse || wfUse == 'O' || wfUse == 'M') {
                //not WF step OR is optional or mandatory wf step
                permittedEventList.push(event);
            }
        }
    }

    private wfEventCleanUp(
        objectCode: string,
        wfStep: any,
        permittedEventList: EventType[],
        mandatoryEventList: EventType[]
    ) {
        let idx = -1;

        //Clean up permittedEvents
        //Accept. Not allowed if Optional and either Submit or Reject are mandatory.
        idx = permittedEventList.indexOf(EventType.Accept);
        if (idx > -1) {
            if (wfStep.Accept_Use == 'O' && (wfStep.Submit_Use == 'M' || wfStep.Reject_Use == 'M')) {
                permittedEventList.splice(idx, 1);
            }
        }

        //Reject. Not allowed if Optional and either Submit or Accept are mandatory.
        idx = permittedEventList.indexOf(EventType.Reject);
        if (idx > -1) {
            if (wfStep.Reject_Use == 'O' && (wfStep.Submit_Use == 'M' || wfStep.Accept_Use == 'M')) {
                permittedEventList.splice(idx, 1);
            }
        }

        //Submit. Not allowed if Accept is mandatory or if Submit is Optional and Reject is mandatory.
        idx = permittedEventList.indexOf(EventType.Submit);
        if (idx > -1) {
            if (wfStep.Accept_Use == 'M' || (wfStep.Submit_Use == 'O' && wfStep.Reject_Use == 'M')) {
                permittedEventList.splice(idx, 1);
            }
        }

        //Remove Rate form mandatoryEventList.
        idx = mandatoryEventList.indexOf(EventType.Rate);
        if (idx > -1) {
            mandatoryEventList.splice(idx, 1);
        }

        //Remove Print form mandatoryEventList. Currently, Print isn't a valid Event_Code for the Event_Log table.
        // So it impossible to complete a task with a Mandatory Print event.
        idx = mandatoryEventList.indexOf(EventType.Print);
        if (idx > -1) {
            mandatoryEventList.splice(idx, 1);
        }
    }

    setSingleSectionMode(on: boolean) {
        if (!on) {
            this.store.dispatch([new SetSingleSectionMode(on), new SetAllSectionsVisible()]);
        } else {
            this.store.dispatch(new SetSingleSectionMode(on));
        }
    }

    setManualNavigationMode(on: boolean) {
        this.store.dispatch(new SetManualNavigationMode(on));
    }

    setDesignerMode(on: boolean) {
        this.store.dispatch(new SetDesignerMode(on));
    }

    setCurrentDesignerComponent(component: DesignerComponent) {
        this.store.dispatch(new SetCurrentDesignerComponent(component));
    }

    setCurrentDesignerComponentEditMode(editMode: boolean) {
        let currentDesignerComponent = this.store.selectSnapshot(PageState.currentDesignerComponent);
        if (currentDesignerComponent && currentDesignerComponent.editMode != editMode) {
            this.store.dispatch(new SetCurrentDesignerComponentEditMode(editMode));
        }
    }

    //TODO: Strongly type the element here and in page state
    setCurrentDesignerElement(element: DesignerElement) {
        this.store.dispatch(new SetCurrentDesignerElement(element));
    }

    setCurrentSection(sectionID: number) {
        this.store.dispatch(new SetCurrentSection(sectionID));
    }

    setCurrentSectionByTitle(title: string) {
        this.store.dispatch(new SetCurrentSectionByTitle(title));
    }

    selectSection(sectionID: number) {
        this.store.dispatch(new SelectSection(sectionID));
    }

    navigatorSectionClicked(section: any) {
        if (section.Child_Section_IDs && section.Child_Section_IDs.length > 0) {
            let currentSection = this.store.selectSnapshot(PageState.currentSection);
            if (
                !section.Component_Type ||
                (section.Component_Type && currentSection && currentSection.Section_ID == section.Section_ID)
            ) {
                this.expandCollapseChildSections(section.Section_ID);
            }
        }

        if (section.Component_Type) {
            //only call selectSection if there is a Component_Type.
            this.selectSection(section.Section_ID);
        }
    }

    setCurrentPageView(viewID: number) {
        this.store.dispatch(new SetCurrentPageView(viewID));
    }

    setPageViewSections(sections: PageSection[]) {
        this.store.dispatch(new SetPageViewSections(sections));
    }

    setCoreSections(coreSections: PageSection[]) {
        this.store.dispatch(new SetCoreSections(coreSections));
    }

    setNavigatorStyle(navigatorStyle: NavigatorStyleType) {
        this.store.dispatch(new SetNavigatorStyle(navigatorStyle));
    }

    setDirty() {
        this.store.dispatch(new SetDirty());
    }

    setPristine() {
        this.store.dispatch(new SetPristine());
    }

    // events is the list of events that are valid for this object.  If WF does not allow the event or the used 
    // doesn't have rights to the event the event should be excluded.
    // saveValidationEvent is the event that should be used when a manual save is done.  
    // Typically the event that was last done on the object.  (e.g. if a contract proc is in submitted state then this would be 'Submit')
    // anticipatedValidationEvent is the anticipated next event for the object based on current state and 
    // what events are allowed in the WF and that user has rights to.  This is used to do inplace validation in the UI (edit panenls)
    setValidEvents(events: EventType[], saveValidationEvent: EventType, anticipatedValidationEvent: EventType) {
        this.store.dispatch(new SetValidEvents(events, saveValidationEvent, anticipatedValidationEvent));
    }

    addValidEvent(event: EventType) {
        this.store.dispatch(new AddValidEvent(event));
    }

    setAnticipatedValidationEvent(anticipatedValidationEvent: EventType) {
        this.store.dispatch(new SetAnticipatedValidationEvent(anticipatedValidationEvent));
    }

    private setPermittedEvents(events: EventType[]) {
        this.store.dispatch(new SetPermittedEvents(events));
    }

    private setMandatoryEvents(events: EventType[]) {
        this.store.dispatch(new SetMandatoryEvents(events));
    }

    setMissingEvents(events: string[]) {
        this.store.dispatch(new SetMissingEvents(events));
    }

    getMissingEvents(): any[] {
        let missingEvents = this.store.selectSnapshot(PageState.missingEvents);
        return missingEvents;
    }

    updateSectionData(sectionKey: any, data: any) {
        this.store.dispatch(new UpdateSectionData(sectionKey, data));
    }

    updateSectionUxConfig(sectionID: number, uxConfig: any) {
        this.store.dispatch(new UpdateSectionUxConfig(sectionID, uxConfig));
    }

    updateSectionUxConfigComponent(
        sectionID: number,
        componentName: string,
        componentConfig: any,
        refreshRuleSets: boolean = false
    ) {
        let uxConfig = { [componentName]: componentConfig };
        this.store.dispatch(new UpdateSectionUxConfig(sectionID, uxConfig, refreshRuleSets));
    }

    updateSectionUxConfigComponentSetting(sectionID: number, componentName: string, settingNode: string, settings: any) {
        this.store.dispatch(new UpdateSectionUxConfigComponentSettings(sectionID, componentName, settingNode, settings));
    }

    async updateSectionComponentSettingLink(sectionID: number, componentSettingLink: ComponentSettingLink) {
        let pageView: PageView = this.store.selectSnapshot(PageState.currentPageView);
        let componentSetting: ComponentSetting = null;
        let settingID = componentSettingLink.Setting_ID;

        if (pageView.ComponentSettings) {
            componentSetting = pageView.ComponentSettings.find((x) => x.Setting_ID == settingID);
        }

        if (!componentSetting && settingID != null && settingID != 'null') {
            // If existing setting is not yet used on this layout then add it to the componenSettings for this layout
            if (await this.loadComponentSetting(settingID)) {
                componentSetting = cloneDeep(this.app.dsComponentSettingGet.objectNode);
                try {
                    componentSetting.Setting_Syntax = JSON.parse(atob(componentSetting.Setting_Syntax));
                } catch {
                    componentSetting.Setting_Syntax = {};
                }
                this.updateComponentSetting(componentSetting, 'N'); // add to componentSetting List with edit status of "Not Changed"
            }
        }
        this.store.dispatch(new UpdateSectionComponentSettingLink(sectionID, componentSettingLink, componentSetting));
    }

    restoreSectionUxConfigComponentSetting(
        sectionID: number,
        componentName: string,
        settingNode: 'All' | 'ChildLayout' | 'RuleSet'
    ) {
        let currentDesignerComponent: DesignerComponent = this.store.selectSnapshot(PageState.currentDesignerComponent);

        if (!currentDesignerComponent || currentDesignerComponent.name != componentName) {
            return;
        }
        if (settingNode == 'All') {
            this.updateSectionUxConfigComponent(sectionID, componentName, currentDesignerComponent.savedConfig, true);
        } else {
            let config = null;
            if (currentDesignerComponent.savedConfig) {
                config = currentDesignerComponent.savedConfig[settingNode];
            }
            this.updateSectionUxConfigComponentSetting(sectionID, componentName, settingNode, config);
        }
    }

    updateSectionUxConfigMainComponent(sectionID: number, componentConfig: any) {
        let uxConfig = { MainComponent: componentConfig };
        this.store.dispatch(new UpdateSectionUxConfig(sectionID, uxConfig));
    }

    updateSectionProperties(section: PageSection) {
        this.store.dispatch(new UpdateSectionProperties(section));
    }

    updateSectionTitle(sectionID: number, title: any) {
        this.store.dispatch(new UpdateSectionTitle(sectionID, title));
    }

    updateComponentSetting(componentSetting: ComponentSetting, forcedEditStatus: string = null) {
        this.store.dispatch(new UpdateComponentSetting(componentSetting, forcedEditStatus));
    }

    resetState() {
        this.clearLock();
        this.store.dispatch(new ResetState());
    }

    resetObjectRules() {
        this.store.dispatch(new ResetObjectRules());
    }

    addObjectRuleSets(objectRuleSets: ObjectRuleSet[], overwrite: boolean = true, addToCurrentPageView: boolean = false) {
        this.store.dispatch(new AddObjectRuleSets(objectRuleSets, overwrite, addToCurrentPageView));
    }

    setCompiledObjectRules(compiledObjectRules: CompiledObjectRules) {
        this.store.dispatch(new SetCompiledRules(compiledObjectRules));
    }

    setEvaluatedStateObjects(evaluatedStateObjects: EvaluatedStateObject[], event: EventType) {
        this.store.dispatch(new SetEvaluatedStateObjects(evaluatedStateObjects, event));
    }

    setObjectValidationEvents(objectValidationEvents: EventType[]) {
        this.store.dispatch(new SetObjectValidationEvents(objectValidationEvents));
    }

    async saveValidationRules() {
        return new Promise(async (resolve) => {
            this.store.dispatch(new SaveValidationRules()).subscribe(() => {
                resolve(true);
            });
        });
    }

    restoreValidationRules() {
        this.store.dispatch(new RestoreValidationRules());
    }

    setUserDefinedValidations(validations: UserDefinedValidation[], callbackFunction: any) {
        this.store.dispatch(new SetUserDefinedValidations(validations));
        if (callbackFunction) {
            this.callbackFunction = callbackFunction;
        }
        this.openUserDefinedValidationDialog();
    }

    openUserDefinedValidationDialog() {
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = 'UserDefinedValidationDialogComponent';
    }

    closeUserDefinedValidationDialog(result: any) {
        this.store.dispatch(new SetUserDefinedValidations([])); //Clear in state

        if (this.modalRef) {
            this.modalRef.close();
        }
        if (this.callbackFunction) {
            this.callbackFunction(result);
        }
        this.callbackFunction = null;
    }

    setAllSectionsReadOnly(readOnly: boolean, excludingSections: string[] = null) {
        this.store.dispatch(new SetAllSectionsReadOnly(readOnly, excludingSections));
        //Note:  Excluding sections is a way to exclude a list of sections from the readonly setting
        //       can be an array of SectionIDs, ComponentTypes, or Section Titles
    }

    updatePageModel(pageModel: any, mergeValues = true) {
        this.store.dispatch(new UpdatePageModel(pageModel, mergeValues));
    }

    resetPageModel(defaults: any = {}) {
        this.store.dispatch(new UpdatePageModel(defaults, false));
    }

    trackUnsavedChanges(on: boolean = true) {
        this.store.dispatch(new TrackUnsavedChanges(on));
    }

    savePageModelToPageParameters() {
        // store all values in pageModel to the current page's parameters.   Model values are prefixed with "Model."
        let pageModel = this.store.selectSnapshot(PageState.pageModel);
        if (pageModel) {
            for (var param in pageModel) {
                this.pageParameterService.setParameter(this.pageID, 'Model.' + param, pageModel[param]);
            }
        }
    }

    restorePageModelFromPageParameters(): any {
        // get all Model.Xxxx page parameters and push them into the pageModel
        let pageModel = {};
        let paramSet = this.pageParameterService.getParameterSet(this.pageID);
        for (var pageParam in paramSet) {
            if (pageParam.startsWith('Model.')) {
                let modelParam = pageParam.slice(6); //strip off "Model."
                if (paramSet[pageParam] == null) {
                    paramSet[pageParam] = '';
                } else {
                    pageModel[modelParam] = paramSet[pageParam];
                }
            }
        }
        this.updatePageModel(pageModel);
        return pageModel;
    }

    setPageID(pageID: string) {
        this.pageID = pageID;
    }

    isLocked(): boolean {
        let locked = false;
        let lockGUID = this.store.selectSnapshot(PageState.lock).Lock_GUID;
        if (lockGUID && lockGUID > '') {
            locked = true;
        }
        return locked;
    }

    isLockedByOtherUser(): boolean {
        let locked = false;
        let lock = this.store.selectSnapshot(PageState.lock);
        if ((!lock.Lock_GUID || lock.Lock_GUID == '') && lock.Lock_User_ID && lock.Lock_User_ID > '') {
            locked = true;
        }
        return locked;
    }

    isObjectLocked(objectID: string, objectCode: string): boolean {
        let locked = false;
        let lock = this.store.selectSnapshot(PageState.lock);
        if (lock && lock.Lock_GUID && lock.Lock_GUID > '') {
            for (var l in lock.Locked_Objects) {
                if (lock.Locked_Objects[l].Object_Code == objectCode && lock.Locked_Objects[l].Object_ID == objectID) {
                    locked = true;
                    break;
                }
            }
        }
        return locked;
    }

    getCurrentLockGUID(): string {
        let lockGUID = this.store.selectSnapshot(PageState.lock).Lock_GUID;
        return lockGUID;
    }

    async clearLock() {
        return new Promise(async (resolve) => {
            if (this.isLocked()) {
                await this.processLock(LockEventType.Delete, []);
                resolve(true);
            } else {
                if (this.isLockedByOtherUser()) {
                    this.store.dispatch(new ResetLock()).subscribe((action) => {
                        resolve(true);
                    });
                } else {
                    resolve(true);
                }
            }
        });
    }

    async lockObject(object: LockObject[], ignoreProtectedReadOnly: boolean = false) {
        return new Promise(async (resolve) => {
            let readOnlyMode = this.store.selectSnapshot(PageState.readOnlyMode);
            let protectedMode = this.store.selectSnapshot(PageState.protectedMode);
            let lockObjects: LockObject[] = [];
            let process = false; // Gets set to true if at least one object is added or deleted from the Lock.
            let event: LockEventType;

            this.dsLock.reset();

            if ((!readOnlyMode && !protectedMode) || ignoreProtectedReadOnly) {
                if (!this.isLocked()) {
                    //Create new Lock...

                    process = true;
                    event = LockEventType.Create;
                    for (var i in object) {
                        lockObjects.push({
                            Edit_Status: LockObjectEditStatusType.Add,
                            Object_ID: object[i].Object_ID,
                            Object_Code: object[i].Object_Code,
                            Lock_Sub_Type: object[i].Lock_Sub_Type
                        });
                    }
                } else {
                    // Update existing Lock...
                    event = LockEventType.Update;
                    let lockedObjects = this.store.selectSnapshot(PageState.lock).Locked_Objects;
                    //Push existing Locked Objects from State...
                    for (var i in lockedObjects) {
                        lockObjects.push({
                            Edit_Status: LockObjectEditStatusType.NoChange,
                            Object_ID: lockedObjects[i].Object_ID,
                            Object_Code: lockedObjects[i].Object_Code,
                            Lock_Sub_Type: lockedObjects[i].Lock_Sub_Type
                        });
                    }

                    let idx = -1;
                    //Objects being added to the Lock...
                    for (var i in object) {
                        idx = lockedObjects.map((record) => record.Object_ID).indexOf(object[i].Object_ID);
                        if (idx == -1) {
                            process = true;
                            lockObjects.push({
                                Edit_Status: LockObjectEditStatusType.Add,
                                Object_ID: object[i].Object_ID,
                                Object_Code: object[i].Object_Code,
                                Lock_Sub_Type: object[i].Lock_Sub_Type
                            });
                        }
                    }

                    //Objects being removed from the Lock...
                    for (var i in lockObjects) {
                        idx = object.map((record) => record.Object_ID).indexOf(lockObjects[i].Object_ID);
                        if (idx == -1) {
                            process = true;
                            lockObjects[i].Edit_Status = LockObjectEditStatusType.Delete;
                        }
                    }
                }

                if (process) {
                    let success = await this.processLock(event, lockObjects);
                    if (this.dsLockSub) {
                        this.dsLockSub.unsubscribe();
                    }

                    if (success) {
                        resolve(true);
                    } else {
                        this.setProtectedMode(true); //Set Protected if Lock Fails...
                        resolve(false); // Resolve as False. LockProcess failed...
                    }
                } else {
                    resolve(true); //Resolve true. Nothing to process, but nothing errored...
                }
            } else {
                resolve(false); // Resolve as False. No locks created or modified when ReadOnly or Protected...
            }
        });
    }

    private async processLock(event: LockEventType, lockObjects: any[], lockGUID: string = '') {
        //Get non-deleted Objects from the Lock and store in State...
        let lockedObjects: any = [];

        return new Promise(async (resolve) => {
            if (this.dsLockSub) {
                this.dsLockSub.unsubscribe();
            }

            if (event != LockEventType.Create) {
                if (!lockGUID || lockGUID == '') {
                    lockGUID = this.getCurrentLockGUID();
                }
            }

            let body = {
                '?xml': { '@version': '1.0' },
                ConceptOneRequest: {
                    LockProcess: {
                        Book_Entity_ID: "1000", //this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
                        Event: event,
                        Lock: {
                            Lock_GUID: lockGUID,
                            Lock_Name: 'SureMGAObjectLock',
                            Lock_Desc: 'Object Locked by Sure MGA',
                            Lock_Object: lockObjects
                        }
                    }
                }
            };

            this.dsLock.process({ json: 'CT2' }, body);

            this.dsLockSub = this.dsLock.status$.subscribe(async (status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        if (this.dsLockSub) {
                            this.dsLockSub.unsubscribe();
                        }

                        if (event == LockEventType.Delete) {
                            this.store.dispatch(new ResetLock()).subscribe((action) => {
                                resolve(true);
                            });
                        } else {
                            for (var i in this.dsLock.objectNode.Lock_Object) {
                                if (this.dsLock.objectNode.Lock_Object[i].Edit_Status != 'D') {
                                    lockedObjects.push({ ...this.dsLock.objectNode.Lock_Object[i] });
                                    delete lockedObjects[lockedObjects.length - 1].Edit_Status;
                                    delete lockedObjects[lockedObjects.length - 1].Process_Status;
                                    delete lockedObjects[lockedObjects.length - 1].Process_Message;
                                }
                            }

                            this.store
                                .dispatch([new SetLock({ Lock_GUID: this.dsLock.objectNode.Lock_GUID, Locked_Objects: lockedObjects })])
                                .subscribe((action) => {
                                    resolve(true);
                                });
                        }

                        break;

                    case DataSourceStatus.Error:
                        if (this.dsLockSub) {
                            this.dsLockSub.unsubscribe();
                        }

                        if (event == LockEventType.Delete) {
                            this.store.dispatch(new ResetLock()).subscribe((action) => {
                                resolve(true);
                            });
                        } else {
                            //Check for Lock_User_ID and Lock_User_Name
                            if (this.dsLock && this.dsLock.tranNode && this.dsLock.tranNode.Lock_User_ID) {
                                //If Lock Creation fails, allow use of existing lock if locked by current user 
                                // and SDK returned the SDK2.0 style Lock_GUID (not a C1 lock)
                                if (
                                    this.dsLock.tranNode.Event == LockEventType.Create &&
                                    this.dsLock.tranNode.Lock_User_ID == this.app.UserID &&
                                    this.dsLock.objectNode.Lock_GUID
                                ) {
                                    //Dialog regarding use of existing lock.
                                    let useExisting = await this.useExistingUserLock();
                                    if (useExisting) {
                                        for (var i in this.dsLock.objectNode.Lock_Object) {
                                            if (this.dsLock.objectNode.Lock_Object[i].Edit_Status != 'D') {
                                                lockedObjects.push({ ...this.dsLock.objectNode.Lock_Object[i] });
                                                delete lockedObjects[lockedObjects.length - 1].Edit_Status;
                                                delete lockedObjects[lockedObjects.length - 1].Process_Status;
                                                delete lockedObjects[lockedObjects.length - 1].Process_Message;
                                            }
                                        }

                                        this.store
                                            .dispatch([
                                                new SetLock({ Lock_GUID: this.dsLock.objectNode.Lock_GUID, Locked_Objects: lockedObjects })
                                            ])
                                            .subscribe((action) => {
                                                window.localStorage.setItem('proceedWithExistingLock', this.dsLock.objectNode.Lock_GUID);
                                                resolve(true);
                                            });
                                    } else {
                                        this.store
                                            .dispatch([
                                                new SetLock({
                                                    Lock_User_ID: this.dsLock.tranNode.Lock_User_ID,
                                                    Lock_User_Name: this.dsLock.tranNode.Lock_User_Name
                                                })
                                            ])
                                            .subscribe((action) => {
                                                resolve(false);
                                            });
                                    }
                                } else {
                                    this.store
                                        .dispatch([
                                            new SetLock({
                                                Lock_User_ID: this.dsLock.tranNode.Lock_User_ID,
                                                Lock_User_Name: this.dsLock.tranNode.Lock_User_Name
                                            })
                                        ])
                                        .subscribe((action) => {
                                            resolve(false);
                                        });
                                }
                            } else {
                                resolve(false);
                            }
                        }
                        break;
                }
            });
        });
    }

    proceedWithExistingLockChanged(event: any) {
        if (event.key === 'proceedWithExistingLock') {
            if (event.newValue == null) {
                return;
            }

            if (this.getCurrentLockGUID() == event.newValue) {
                this.store.dispatch([new ResetLock()]);
                this.setProtectedMode(true);
            }
        }
    }

    async useExistingUserLock() {
        return new Promise(async (resolve) => {
            let message =
                'Using the existing lock while having the object open elsewhere may overwrite changes or cause errors.';
            message += '\nThis should only be done after confirming you do not have the object open elsewhere.';
            message += '\n\nProceed to edit with existing lock?';

            this.confirmationDialog
                .confirm('Existing Lock Detected', message, 'Yes', 'No', 'lg')
                .then((confirmed) => {
                    if (confirmed) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                })
                .catch(() => {
                    resolve(false);
                });
        });
    }

    removeUserLock() {
        let lockedBy = this.dsLock.tranNode.Lock_User_Name;
        let lockDate = this.epDate.transform(this.dsLock.tranNode.Lock_Date, 'mm/dd/yyyy hh:mm ampm', true);
        if (this.app.UserID == this.dsLock.tranNode.Lock_User_ID) {
            //Get non-deleted Objects from the Lock and store in State...
            let lockedObjects: any = [];
            this.useExistingUserLock().then((useExisting) => {
                if (useExisting) {
                    for (var i in this.dsLock.objectNode.Lock_Object) {
                        if (this.dsLock.objectNode.Lock_Object[i].Edit_Status != 'D') {
                            lockedObjects.push({ ...this.dsLock.objectNode.Lock_Object[i] });
                            delete lockedObjects[lockedObjects.length - 1].Edit_Status;
                            delete lockedObjects[lockedObjects.length - 1].Process_Status;
                            delete lockedObjects[lockedObjects.length - 1].Process_Message;
                        }
                    }

                    this.store
                        .dispatch([new SetLock({ Lock_GUID: this.dsLock.objectNode.Lock_GUID, Locked_Objects: lockedObjects })])
                        .subscribe((action) => {
                            window.localStorage.setItem('proceedWithExistingLock', this.dsLock.objectNode.Lock_GUID);
                            this.setProtectedMode(false);
                            this.setReadOnlyMode(false);
                            this.manualEditTracker.next(null);
                        });
                } else {
                    this.store.dispatch([
                        new SetLock({
                            Lock_User_ID: this.dsLock.tranNode.Lock_User_ID,
                            Lock_User_Name: this.dsLock.tranNode.Lock_User_Name
                        })
                    ]);
                }
            });
            // TODO: Shared Auth - remove true and rely on permissions
        } else if (true || this.app.getUserOptionValue('Admin_Ind') == 'Y') {
            let message = `This page is currently being edited by ${lockedBy} and was locked at ${lockDate}.`;
            message += `\n\nProceeding without confirming that the page is not open elsewhere may result in overwriting changes or causing errors.`;
            this.confirmationDialog
                .confirm('Page Locked', message, 'Override Lock', 'Cancel', 'lg')
                .then((confirmed) => {
                    if (confirmed) {
                        this.processLock(LockEventType.Delete, [], this.dsLock.tranNode.Lock.Lock_GUID).then((r) => {
                            this.setProtectedMode(false);
                            this.setReadOnlyMode(false);
                            this.manualEditTracker.next(null);
                        });
                    }
                })
                .catch(() => { });
        } else {
        }
    }

    currentSectionSnapshot(): PageSection {
        return this.store.selectSnapshot(PageState.currentSection);
    }

    async getPageLayout(layoutObjectCode) {
        return new Promise(async (resolve) => {
            if (this.dsPageLayoutSub) {
                this.dsPageLayoutSub.unsubscribe();
            }

            this.app.dsPageLayoutGet.get({ json: 'CT2', Layout_Object_Code: layoutObjectCode });

            this.dsPageLayoutSub = this.app.dsPageLayoutGet.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        if (this.dsPageLayoutSub) {
                            this.dsPageLayoutSub.unsubscribe();
                        }
                        resolve(true);
                        break;

                    case DataSourceStatus.Error:
                        if (this.dsPageLayoutSub) {
                            this.dsPageLayoutSub.unsubscribe();
                        }
                        resolve(false);
                        break;
                }
            });
        });
    }

    async getPageLayoutCollection() {
        return new Promise(async (resolve) => {
            this.pageViewList = [];
            if (this.dsPageLayoutCollectionSub) {
                this.dsPageLayoutCollectionSub.unsubscribe();
            }

            if (this.app.DesignerMode) {
                this.app.dsPageLayoutCollection.objectType = 'PageLayoutConfig';
            } else {
                this.app.dsPageLayoutCollection.objectType = 'PageLayout';
            }

            //TODO: Move this datasource inside of this service rather than in App service
            this.app.dsPageLayoutCollection.getCollection({
                json: 'CT2',
                View: 'Full',
                Parent_Object_Code: this.baseObjectCode,
                Object_Code: this.objectCode,
                Disabled_Ind: 'Y;N'
            });

            this.dsPageLayoutCollectionSub = this.app.dsPageLayoutCollection.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        if (this.dsPageLayoutCollectionSub) {
                            this.dsPageLayoutCollectionSub.unsubscribe();
                        }
                        this.pageViewList = this.app.dsPageLayoutCollection.objectNodes;
                        resolve(true);
                        break;

                    case DataSourceStatus.Error:
                        if (this.dsPageLayoutCollectionSub) {
                            this.dsPageLayoutCollectionSub.unsubscribe();
                        }
                        resolve(false);
                        break;
                }
            });
        });
    }

    savePageLayout(pageViewName: string = null, saveMode: 'SAVE' | 'CLONE' | 'NEW' = 'SAVE') {
        let currentPageView = this.store.selectSnapshot(PageState.currentPageView);
        let sections = this.store.selectSnapshot(PageState.sections);
        //let componentSettings = this.store.selectSnapshot(PageState.componentSettings);
        let order = 0;
        let saveAs = saveMode == 'NEW' || saveMode == 'CLONE';

        let componentSettingList: ComponentSetting[] = [];
        let sectionList: any[] = [];

        if (saveMode != 'NEW') {
            //Note: NEW will create a page view without any sections
            componentSettingList = cloneDeep(currentPageView.ComponentSettings) || [];

            //base64 encode setting syntax for all component settings
            componentSettingList.forEach((element) => {
                if (element.Setting_Syntax) {
                    //remove setting ID from the syntax node
                    delete element.Setting_Syntax.Setting_ID;
                }
                element.Setting_Syntax = btoa(JSON.stringify(element.Setting_Syntax));
            });

            //only save of selected section properties.  Do not save internal processing properties.
            //NOTE: Internal state properties names are translated to SDK element names
            sectionList = sections.map((s) => {
                //Temp var where we strip out non-MainComponent components before save (these are saved in component settings)
                let cleanedUpUxConfig = cloneDeep(s.Ux_Config) || {};
                let dbSectionID = s.DB_Section_ID;
                let sectionEditStatus = s.Edit_Status || 'C';
                if (saveAs) {
                    sectionEditStatus = 'A';
                }
                if (sectionEditStatus != 'A' && sectionEditStatus != 'D') {
                    sectionEditStatus = 'C'; //force section to change for now.   We are always updating the component settings
                }

                if (saveAs || s.Edit_Status == 'A') {
                    dbSectionID = null;
                }

                let sectionComponentSettingLinks: ComponentSettingLink[] = [];
                for (var componentName in s.Ux_Config) {
                    if (componentName == 'MainComponent') {
                        //Main component stays in the Section's Setting_Syntax  (all other components are moved to component_settings)
                        continue;
                    }
                    let settingID = s.Ux_Config[componentName].Setting_ID;

                    let settingSyntax = cloneDeep(s.Ux_Config[componentName]);
                    //don't keep Setting_ID in the stored syntax -- Setting_ID is only here temporarily (until full component settings is implemented)
                    delete settingSyntax.Setting_ID;

                    if (!settingID) {
                        settingID = this.helper.getTempID();
                    }

                    if (saveAs) {
                        sectionComponentSettingLinks.push({
                            Component_Name: componentName,
                            Setting_ID: settingID,
                            Edit_Status: settingID > 0 && !saveAs ? 'C' : 'A'
                        });
                    }

                    //remove component setting from Ux_Config   
                    // -- when we support component setting templates we will likely not ever put theses in ux_Config
                    delete cleanedUpUxConfig[componentName];
                }

                if (!saveAs) {
                    sectionComponentSettingLinks = s.ComponentSettings || [];
                }

                return {
                    Section_Name: s.Title, //rename Title to Section_Name (naming dif between state and SDK)
                    Component_Name: s.Component_Type, //rename Component_Type to Component_Name (naming dif between state and SDK)
                    //Layouts: s.Layouts, // ********* TODO: remove and read from core section
                    Default_Edit_Layout: s.Default_Edit_Layout,
                    Default_ReadOnly_Layout: s.Default_ReadOnly_Layout,
                    Section_Group: s.Section_Group || 'GENERAL', //HACK C949B966-CAD3-4F34-9D40-2C06DC6FA48E - SDK is requiring section group,  why?
                    Section_Key: s.Section_Key || '<ALL>', //HACK C949B966-CAD3-4F34-9D40-2C06DC6FA48E - SDK is requiring section key, why?
                    //Help_ID: s.Help_ID, // ************ TODO: remove and read from core section
                    Setting_Syntax: cleanedUpUxConfig ? btoa(JSON.stringify(cleanedUpUxConfig)) : '', // rename Ux_Config to Setting_Syntax
                    Edit_Status: saveAs ? 'A' : sectionEditStatus,
                    Section_ID: dbSectionID, //State section_id is not the same as the section id in the SDK/Database
                    Core_Section_GUID: s.Core_Section_GUID,
                    Section_GUID: s.Section_GUID,
                    Section_Order: s.Display_Order, //state uses display_order and SDK used Section_Order
                    ComponentSettings:
                        sectionComponentSettingLinks.length > 0 ? { ComponentSetting: sectionComponentSettingLinks } : ''
                };
            });
        }
        if (!pageViewName) {
            pageViewName = currentPageView.Title;
        }

        if (this.dsPageLayoutProcessSub) {
            this.dsPageLayoutProcessSub.unsubscribe();
        }

        let transaction = {
            ConceptOneRequest: {
                PageLayoutConfigProcess: {
                    Event: saveAs ? EventType.Create : EventType.Update,
                    PageLayout: {
                        Layout_Object_Code: saveAs ? null : currentPageView.Layout_Object_Code,
                        Parent_Object_Code: this.baseObjectCode,
                        Layout_Name: pageViewName,
                        //RuleSets: [], // Not supporting non-section based user defined rule set.  
                        // if/when we support neet to make sure we do not save based rule sets (core.xxx)
                        Sections: { Section: sectionList },
                        ComponentSettings: { ComponentSetting: componentSettingList }
                    }
                }
            }
        };

        this.app.dsPageLayoutProcess.process({ json: 'CT2', View: 'Full' }, transaction);

        this.dsPageLayoutProcessSub = this.app.dsPageLayoutProcess.status$.subscribe(async (status) => {
            switch (status) {
                case DataSourceStatus.Ready:
                    if (this.dsPageLayoutProcessSub) {
                        this.dsPageLayoutProcessSub.unsubscribe();
                    }
                    // reload page views
                    this.reloadLayoutObjectCode = this.app.dsPageLayoutProcess.objectNode.Layout_Object_Code;
                    this.setPristine();
                    await this.TEMPflushPageLayoutCache();
                    this.loadPageViews(this.baseObjectCode, this._systemPageView, null, null, true);
                    break;

                case DataSourceStatus.Error:
                    if (this.dsPageLayoutProcessSub) {
                        this.dsPageLayoutProcessSub.unsubscribe();
                    }
                    break;
            }
        });
    }

    deletePageLayout(layoutObjectCode: string) {
        let currentPageView = this.store.selectSnapshot(PageState.currentPageView);

        if (this.dsPageLayoutProcessSub) {
            this.dsPageLayoutProcessSub.unsubscribe();
        }

        let transaction = {
            ConceptOneRequest: {
                PageLayoutConfigProcess: {
                    Event: EventType.Delete,
                    PageLayout: {
                        Layout_Object_Code: layoutObjectCode,
                        Parent_Object_Code: this.baseObjectCode
                    }
                }
            }
        };

        this.app.dsPageLayoutProcess.process({ json: 'CT2', View: 'Full' }, transaction);

        this.dsPageLayoutProcessSub = this.app.dsPageLayoutProcess.status$.subscribe(async (status) => {
            switch (status) {
                case DataSourceStatus.Ready:
                    if (this.dsPageLayoutProcessSub) {
                        this.dsPageLayoutProcessSub.unsubscribe();
                    }
                    // reload page views
                    this.reloadLayoutObjectCode == null;
                    await this.TEMPflushPageLayoutCache();
                    this.loadPageViews(this.baseObjectCode, this._systemPageView, null, null, true);
                    break;

                case DataSourceStatus.Error:
                    if (this.dsPageLayoutProcessSub) {
                        this.dsPageLayoutProcessSub.unsubscribe();
                    }
                    break;
            }
        });
    }

    setElementPropertyRule(
        designerComponent: DesignerComponent,
        designerElement: DesignerElement,
        designerElementPropertyType: DesignerElementPropertyType,
        propertyValue: string
    ) {
        // If no variables in the property then wrap in quotes to make a literal string  property.label('My Label')
        if (propertyValue == null || propertyValue.indexOf('[$') == -1) {
            propertyValue = `'${propertyValue}'`;
        }

        let ruleSyntax = `property.${designerElementPropertyType}(${propertyValue})`;
        this.store.dispatch(new AddDesignerElementRule(designerComponent, designerElement, { rule: ruleSyntax }));
    }

    addElementRule(designerComponent: DesignerComponent, designerElement: DesignerElement, ruleNode: RuleNode) {
        this.store.dispatch(new AddDesignerElementRule(designerComponent, designerElement, ruleNode));
    }

    updateElementRule(designerComponent: DesignerComponent, designerElement: DesignerElement, ruleNode: RuleNode) {
        this.store.dispatch(new UpdateDesignerElementRule(designerComponent, designerElement, ruleNode));
    }

    deleteElementRule(designerComponent: DesignerComponent, designerElement: DesignerElement, ruleNode: RuleNode) {
        this.store.dispatch(new DeleteDesignerElementRule(designerComponent, designerElement, ruleNode));
    }

    setPendingDesignerRuleChange() {
        // This method is used to suspend actions by ux refresh subscribers until all state changes are complete
        // Subscribers can check the PendingDesignerRuleChange state property to preempt action.
        // When rules are finally applied this value is turned off automaically in state.
        this.store.dispatch(new SetPendingDesignerRuleChange());
    }

    keepTaskOpen(event: any, terminatingEvents: any[], taskOnly: boolean): string {
        //This method is used by PrepareRequest state actions to set the Keep_Task_Open value.
        let keepTaskOpen = true;
        let isTerminating = false;
        let mandatoryPass = true;
        let uniqueEvents: any = [];
        let isAccountingObject = false;
        let tranEvents: any = [];
        let missingEvents: any = [];

        let permittedEvents = this.store.selectSnapshot(PageState.permittedEvents);

        terminatingEvents.forEach((x) => {
            if (event.includes(x)) {
                isTerminating = true;
            }
        });

        if (isTerminating && event.includes(EventType.Delete)) {
            if (permittedEvents && permittedEvents.includes(EventType.Delete)) {
                //Maybe overkill..? The request should never be built with Delete unless Permitted.
                this.setMissingEvents([]);
                return 'N'; // Delete event, if allowed, will skip checking mandatory events and return 'N'.
            }
        }

        //Check if Mandatory WF step events have been met
        //Only check mandatory events if a terminating event is being used.
        if (isTerminating) {
            let taskObjectEvents = this.store.selectSnapshot(TaskObjectState.objectEvents);
            let currentInfo = this.store.selectSnapshot(TaskObjectState.currentInfo);
            let mandatoryEvents = this.store.selectSnapshot(PageState.mandatoryEvents);

            if (mandatoryEvents.length > 0) {
                taskObjectEvents.forEach((e) => {
                    if (!uniqueEvents.includes(e)) {
                        uniqueEvents.push(e);
                    }
                });

                tranEvents = event.split(';'); //Split into array

                if (!taskOnly) {
                    if (!uniqueEvents.includes('CREATE')) {
                        //Always auto-add create. TODO: Do this or just not enforce mandatory Create. 
                        // All terminiating events will log Create if not already created.
                        uniqueEvents.push('CREATE');
                    }

                    if (!uniqueEvents.includes('MODIFY')) {
                        //Always auto-add modify. TODO: Do this or just not enforce mandatory Modify. All terminiating events will log a modify event.
                        uniqueEvents.push('MODIFY');
                    }

                    for (var i in tranEvents) {
                        //Add the Events that the transaction will do (if successful) to the list that "has been" done.
                        if (['SEND', 'DISTRIBUTE'].includes(tranEvents[i].toUpperCase())) {
                            if (!uniqueEvents.includes('FILE')) {
                                uniqueEvents.push('FILE');
                            }
                            if (!uniqueEvents.includes('SEND')) {
                                uniqueEvents.push('SEND');
                            }
                        } else if (
                            currentInfo.Task_Object_Code.toLowerCase() == 'contprint' &&
                            tranEvents[i].toUpperCase() == 'ACCEPT'
                        ) {
                            if (!uniqueEvents.includes('FILE')) {
                                uniqueEvents.push('FILE');
                            }
                            if (!uniqueEvents.includes('SEND')) {
                                uniqueEvents.push('SEND');
                            }
                        } else if (tranEvents[i].toUpperCase() == 'UPDATE') {
                            //Skip Updated since Modify already added.
                        } else {
                            if (!uniqueEvents.includes(tranEvents[i].toUpperCase())) {
                                uniqueEvents.push(tranEvents[i].toUpperCase());
                            }
                        }
                    }
                }

                if (
                    [
                        '13thperiod',
                        'balforward',
                        'bill',
                        'clmbill',
                        'clmrec',
                        'deposit',
                        'disburse',
                        'jrnlentry',
                        'invoice',
                        'latecharge',
                        'receipt',
                        'reconcile',
                        'periodend',
                        'lockbox'
                    ].includes(currentInfo.Task_Object_Code.toLowerCase())
                ) {
                    isAccountingObject = true;
                }

                for (var i in mandatoryEvents) {
                    if (['Submit', 'Accept', 'Reject'].includes(mandatoryEvents[i])) {
                        if (isAccountingObject) {
                            //Check each individually for accounting objects.
                            if (!uniqueEvents.includes(mandatoryEvents[i].toUpperCase())) {
                                mandatoryPass = false;
                                missingEvents.push(mandatoryEvents[i]);
                            }
                        }
                    } else if (mandatoryEvents[i] == EventType.Update) {
                        if (!uniqueEvents.includes('MODIFY')) {
                            mandatoryPass = false;
                            missingEvents.push(mandatoryEvents[i]);
                        }
                    } else if (mandatoryEvents[i] == EventType.Send && isAccountingObject) {
                        if (!uniqueEvents.includes('FILE')) {
                            mandatoryPass = false;
                            missingEvents.push('Send/Distribute');
                        }
                    } else {
                        if (!uniqueEvents.includes(mandatoryEvents[i].toUpperCase())) {
                            mandatoryPass = false;
                            missingEvents.push(mandatoryEvents[i]);
                        }
                    }
                }
                //Submit, Accept, Reject check for non accounting objects.
                if (!isAccountingObject) {
                    if (mandatoryEvents.includes(EventType.Accept) && mandatoryEvents.includes(EventType.Reject)) {
                        // If both accept and reject are mandatory then one or the other needs to have been triggered.
                        if (!uniqueEvents.includes('ACCEPT') && !uniqueEvents.includes('REJECT')) {
                            mandatoryPass = false;
                            missingEvents.push('Accept or Reject');
                        }
                    } else if (mandatoryEvents.includes(EventType.Submit) && mandatoryEvents.includes(EventType.Reject)) {
                        // If both submit and reject are mandatory then one or the other needs to have been triggered.
                        if (!uniqueEvents.includes('SUBMIT') && !uniqueEvents.includes('REJECT')) {
                            mandatoryPass = false;
                            missingEvents.push('Submit or Reject');
                        }
                    } else if (mandatoryEvents.includes(EventType.Submit) && mandatoryEvents.includes(EventType.Accept)) {
                        // If both submit and accept are mandatory then only check that ACCEPT has been triggered.
                        if (!uniqueEvents.includes('ACCEPT')) {
                            mandatoryPass = false;
                            missingEvents.push('Accept');
                        }
                    } else {
                        // Only one of the three (accept,reject,submit) or none are marked mandatory.  Check each individually.
                        if (mandatoryEvents.includes(EventType.Submit) && !uniqueEvents.includes('SUBMIT')) {
                            mandatoryPass = false;
                            missingEvents.push('Submit');
                        }
                        if (mandatoryEvents.includes(EventType.Accept) && !uniqueEvents.includes('ACCEPT')) {
                            mandatoryPass = false;
                            missingEvents.push('Accept');
                        }
                        if (mandatoryEvents.includes(EventType.Reject) && !uniqueEvents.includes('REJECT')) {
                            mandatoryPass = false;
                            missingEvents.push('Reject');
                        }
                    }
                }
            }
        }

        //If Delete was the only missing Mandatory Event, reset mandatoryPass and don't keep Task Open.
        if (missingEvents.length == 1 && missingEvents[0] == EventType.Delete) {
            mandatoryPass = true;
            missingEvents = [];
        }

        this.setMissingEvents(missingEvents);
        keepTaskOpen = !isTerminating || !mandatoryPass;

        return keepTaskOpen ? 'Y' : 'N';
    }

    openPageSectionsDialog() {
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static',
            windowClass: 'ep-modal-panel-double'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = 'UxDesignerPageSectionDialogComponent';
        this.modalRef.componentInstance.dynamicData = { modal: true };
    }

    closePageSectionsDialog(sections?: PageSection[]) {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (sections) {
            this.addSections(sections, true);
        }
    }

    openCollectionConfigDialog(baseColumnDef: any, currentColumnDef: any, callbackFunction: any) {
        if (callbackFunction) {
            this.callbackFunction = callbackFunction;
        }

        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'md',
            injector: this.injector,
            backdrop: 'static',
            windowClass: 'ep-modal-panel-double'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = 'UxDesignerCollectionConfigDialogComponent';
        this.modalRef.componentInstance.dynamicData = {
            modal: true,
            baseColumnDef: baseColumnDef,
            currentColumnDef: currentColumnDef
        };
    }

    closeCollectionConfigDialog(result?: any) {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (this.callbackFunction) {
            this.callbackFunction(result);
        }
        this.callbackFunction = null;
    }

    openLayoutConfigDialog() {
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static',
            windowClass: 'ep-modal-panel-double'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = 'UxDesignerLayoutConfigDialogComponent';
    }

    closeLayoutConfigDialog() {
        if (this.modalRef) {
            this.modalRef.close();
        }
    }

    /* This method is is a shortcut so other components do not need to import Store or PageState. */
    getCurrentPageSection(): PageSection {
        return this.store.selectSnapshot(PageState.currentSection);
    }

    /* This method is is a shortcut so other components do not need to import Store or PageState. */
    getSectionUxdMainComponentConfig(): any {
        let pageSection = this.store.selectSnapshot(PageState.currentSection);
        return cloneDeep(pageSection?.Ux_Config?.MainComponent || null);
    }

    public get layoutLoading(): boolean {
        let count = 0;
        let latestProcessTimestamp: number = 0;

        if (this.app.dsPageLayoutCollection.status == 'Loading') {
            count = count + 1;
        }
        if (this.app.dsPageLayoutGet.status == 'Loading') {
            count = count + 1;
        }
        if (this.app.dsPageLayoutProcess.status == 'Loading') {
            count = count + 1;
            if (this.app.dsPageLayoutProcess.readyTimeStamp) {
                latestProcessTimestamp = this.app.dsPageLayoutProcess.readyTimeStamp;
            }
        }

        if (latestProcessTimestamp > 0) {
            // Delay clearing proceessing after a successful PROCESS by 100ms in case there is a subsequent GET 
            // -- Prevents momentary flicker of User Input Blocking between success of PROCESS and start of GET
            if (moment.now() - latestProcessTimestamp < 100) {
                count = count + 1; //fudge and increase processing count by 1 if last successful PROCESS finished less that 100MS ago
            }
        }
        return count > 0;
    }

    //!!HACK 8EF8843B-12DE-4D9E-9FA7-E7518A741718
    //TEMP CODE -- NEED TO REFACTOR HOW PAGE LAYOUTS ARE CACHED
    //ALSO LOOK AT THIS HACK ID IN SETTINGS.COMPONENT
    TEMPflushPageLayoutCache() {
        //delete all datacache entries
        this.sessionStorage.removeItemOfType('datacache:pagelayout');
        this.sessionStorage.removeItemOfType('datacache:componentsettingconfig');
    }
    //!!END HACK 8EF8843B-12DE-4D9E-9FA7-E7518A741718

    async retrieveProductLineArticle() {
        return new Promise<void>(async (resolve) => {
            this.removeProductLineArticleSub();

            if (this.dsProductLineArticle?.objectNodes?.length > 0) {
                resolve();
            } else {
                this.dsProductLineArticleSub = this.dsProductLineArticle.status$.subscribe((status) => {
                    switch (status) {
                        case DataSourceStatus.Ready:
                            this.removeProductLineArticleSub();

                            resolve();
                            break;

                        case DataSourceStatus.Error:
                            this.removeProductLineArticleSub();
                            resolve();
                            break;
                    }
                });
                this.dsProductLineArticle.get({ Procedure_Name: 'epx_getproductlinearticles' }, true, false);
            }
        });
    }

    removeProductLineArticleSub() {
        if (this.dsProductLineArticleSub) {
            this.dsProductLineArticleSub.unsubscribe();
        }
    }
}
